import type { BetterFetch, BetterFetchOption } from "@better-fetch/fetch";
import type { Store } from "@di-devs/better-feature/types";
import { atom } from "nanostores";
import type { z } from "zod";
import { systemSettingsEndpointConfig } from "../../shared/contracts/systemSettings.contracts";
import {
	type GetSystemSetting200ResponseSchema,
	UpdateSystemSettingRequestSchema,
} from "../../shared/schemas/systemSettingsApiSchemas";
import type {
	BulkUpdateSystemSettingsRequest,
	DeleteSystemSettingResponse,
	FileUploadResponse,
	GetSystemSettingsResponse,
	SetSystemSettingResponse,
	SystemSetting,
	SystemSettingsMap,
	UpdateSystemSettingRequest,
} from "../../shared/types/systemSettings.types";
import {
	createQuerySetMap,
	extractListeners,
	extractQueries,
	extractSignals,
} from "../shared/queryFactory";

export function createSystemSettingsPlugin(
	$fetch: BetterFetch,
	_$store?: Store,
) {
	const querySets = createQuerySetMap(systemSettingsEndpointConfig, $fetch);

	// Define atoms for system settings state
	const $allSettings = atom<GetSystemSettingsResponse | null>(null);
	const $publicSettings = atom<SystemSettingsMap | null>(null);
	const $currentSetting = atom<SystemSetting | null>(null);
	const $favicon = atom<FileUploadResponse | null>(null);
	const $logo = atom<FileUploadResponse | null>(null);
	const $storePic = atom<FileUploadResponse | null>(null);
	const $isLoading = atom<boolean>(false);
	const $error = atom<string | null>(null);

	return {
		signals: extractSignals(querySets),
		queries: extractQueries(querySets),
		listeners: extractListeners(querySets),
		atoms: {
			$allSettings,
			$publicSettings,
			$currentSetting,
			$favicon,
			$logo,
			$storePic,
			$isLoading,
			$error,
		},
		actions: {
			async getAllSettings() {
				$isLoading.set(true);
				$error.set(null);

				try {
					const result = await $fetch<GetSystemSettingsResponse>(
						systemSettingsEndpointConfig.getSystemSettings.path,
						{
							method: systemSettingsEndpointConfig.getSystemSettings.method,
						},
					);

					// if (result?.data) {
					// 	$allSettings.set(result.data);
					// }

					return result;
				} catch (error) {
					const errorMessage =
						error instanceof Error
							? error.message
							: "Failed to get system settings";
					$error.set(errorMessage);
					throw error;
				} finally {
					$isLoading.set(false);
				}
			},

			async getPublicSettings() {
				$isLoading.set(true);
				$error.set(null);

				try {
					const result = await $fetch<SystemSettingsMap>(
						systemSettingsEndpointConfig.getPublicSettings.path,
						{
							method: systemSettingsEndpointConfig.getPublicSettings.method,
						},
					);

					// if (result?.data) {
					// 	$publicSettings.set(result.data);
					// }

					return result;
				} catch (error) {
					const errorMessage =
						error instanceof Error
							? error.message
							: "Failed to get public settings";
					$error.set(errorMessage);
					throw error;
				} finally {
					$isLoading.set(false);
				}
			},

			async getSetting(settingName: string) {
				$isLoading.set(true);
				$error.set(null);

				try {
					const result = await $fetch<
						z.infer<typeof GetSystemSetting200ResponseSchema>
					>(
						systemSettingsEndpointConfig.getSystemSetting.path.replace(
							":name",
							settingName,
						),
						{
							method: systemSettingsEndpointConfig.getSystemSetting.method,
						},
					);

					// if (result?.data) {
					// 	$currentSetting.set(result.data as unknown as SystemSetting);
					// }

					return result;
				} catch (error) {
					const errorMessage =
						error instanceof Error ? error.message : "Failed to get setting";
					$error.set(errorMessage);
					throw error;
				} finally {
					$isLoading.set(false);
				}
			},

			async setSetting(settingName: string, data: UpdateSystemSettingRequest) {
				$isLoading.set(true);
				$error.set(null);

				// TODO: Apply schema validation to other request bodies
				const validatedData = UpdateSystemSettingRequestSchema.parse(data);
				try {
					const result = await $fetch<
						SetSystemSettingResponse,
						unknown,
						any,
						unknown,
						unknown,
						BetterFetchOption<UpdateSystemSettingRequest>
					>(
						systemSettingsEndpointConfig.setSystemSetting.path.replace(
							":name",
							settingName,
						),
						{
							method: systemSettingsEndpointConfig.setSystemSetting.method,
							body: validatedData,
						},
					);

					// Refresh settings after update
					// await this.getAllSettings();

					return result;
				} catch (error) {
					const errorMessage =
						error instanceof Error ? error.message : "Failed to set setting";
					$error.set(errorMessage);
					throw error;
				} finally {
					$isLoading.set(false);
				}
			},

			async setBulkSettings(data: BulkUpdateSystemSettingsRequest) {
				$isLoading.set(true);
				$error.set(null);

				try {
					const result = await $fetch<SetSystemSettingResponse>(
						systemSettingsEndpointConfig.setBulkSystemSettings.path,
						{
							method: systemSettingsEndpointConfig.setBulkSystemSettings.method,
							body: data,
						},
					);

					// Refresh settings after bulk update
					// await this.getAllSettings();

					return result;
				} catch (error) {
					const errorMessage =
						error instanceof Error
							? error.message
							: "Failed to set bulk settings";
					$error.set(errorMessage);
					throw error;
				} finally {
					$isLoading.set(false);
				}
			},

			async deleteSetting(settingName: string) {
				$isLoading.set(true);
				$error.set(null);

				try {
					const result = await $fetch<DeleteSystemSettingResponse>(
						systemSettingsEndpointConfig.deleteSystemSetting.path.replace(
							":name",
							settingName,
						),
						{
							method: systemSettingsEndpointConfig.deleteSystemSetting.method,
						},
					);

					// Clear current setting if it's the one being deleted
					const current = $currentSetting.get();
					if (current && current.name === settingName) {
						$currentSetting.set(null);
					}

					// Refresh settings after delete
					// await this.getAllSettings();

					return result;
				} catch (error) {
					const errorMessage =
						error instanceof Error ? error.message : "Failed to delete setting";
					$error.set(errorMessage);
					throw error;
				} finally {
					$isLoading.set(false);
				}
			},

			async getFavicon() {
				$isLoading.set(true);
				$error.set(null);

				try {
					const result = await $fetch<FileUploadResponse>(
						systemSettingsEndpointConfig.getFavicon.path,
						{
							method: systemSettingsEndpointConfig.getFavicon.method,
						},
					);

					// if (result?.data) {
					// 	$favicon.set(result.data);
					// }

					return result;
				} catch (error) {
					const errorMessage =
						error instanceof Error ? error.message : "Failed to get favicon";
					$error.set(errorMessage);
					throw error;
				} finally {
					$isLoading.set(false);
				}
			},

			async setFavicon(file: FormData) {
				$isLoading.set(true);
				$error.set(null);

				try {
					const result = await $fetch<SetSystemSettingResponse>(
						systemSettingsEndpointConfig.setFavicon.path,
						{
							method: systemSettingsEndpointConfig.setFavicon.method,
							body: file,
						},
					);

					// Refresh favicon after upload
					// await this.getFavicon();

					return result;
				} catch (error) {
					const errorMessage =
						error instanceof Error ? error.message : "Failed to set favicon";
					$error.set(errorMessage);
					throw error;
				} finally {
					$isLoading.set(false);
				}
			},

			async getLogo() {
				$isLoading.set(true);
				$error.set(null);

				try {
					const result = await $fetch<FileUploadResponse>(
						systemSettingsEndpointConfig.getLogo.path,
						{
							method: systemSettingsEndpointConfig.getLogo.method,
						},
					);

					// if (result?.data) {
					// 	$logo.set(result.data);
					// }

					return result;
				} catch (error) {
					const errorMessage =
						error instanceof Error ? error.message : "Failed to get logo";
					$error.set(errorMessage);
					throw error;
				} finally {
					$isLoading.set(false);
				}
			},

			async setLogo(file: FormData) {
				$isLoading.set(true);
				$error.set(null);

				try {
					const result = await $fetch<SetSystemSettingResponse>(
						systemSettingsEndpointConfig.setLogo.path,
						{
							method: systemSettingsEndpointConfig.setLogo.method,
							body: file,
						},
					);

					// Refresh logo after upload
					// await this.getLogo();

					return result;
				} catch (error) {
					const errorMessage =
						error instanceof Error ? error.message : "Failed to set logo";
					$error.set(errorMessage);
					throw error;
				} finally {
					$isLoading.set(false);
				}
			},

			async getStorePic() {
				$isLoading.set(true);
				$error.set(null);

				try {
					const result = await $fetch<FileUploadResponse>(
						systemSettingsEndpointConfig.getStorePic.path,
						{
							method: systemSettingsEndpointConfig.getStorePic.method,
						},
					);

					// if (result?.data) {
					// 	$storePic.set(result.data);
					// }

					return result;
				} catch (error) {
					const errorMessage =
						error instanceof Error
							? error.message
							: "Failed to get store picture";
					$error.set(errorMessage);
					throw error;
				} finally {
					$isLoading.set(false);
				}
			},

			async setStorePic(file: FormData) {
				$isLoading.set(true);
				$error.set(null);

				try {
					const result = await $fetch<SetSystemSettingResponse>(
						systemSettingsEndpointConfig.setStorePic.path,
						{
							method: systemSettingsEndpointConfig.setStorePic.method,
							body: file,
						},
					);

					// Refresh store picture after upload
					// await this.getStorePic();

					return result;
				} catch (error) {
					const errorMessage =
						error instanceof Error
							? error.message
							: "Failed to set store picture";
					$error.set(errorMessage);
					throw error;
				} finally {
					$isLoading.set(false);
				}
			},

			// Utility actions
			setCurrentSetting(setting: SystemSetting | null) {
				$currentSetting.set(setting);
			},

			setLoading(isLoading: boolean) {
				$isLoading.set(isLoading);
			},

			setError(error: string | null) {
				$error.set(error);
			},
		},
		querySets, // Expose the raw querySets with proper typing
	};
}
